import React, { useContext, useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { propEq, any, map, pluck } from "ramda";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import CollectionCard from "./CollectionCard";
// import IconButton from "@mui/material/IconButton";
import TablePagination from "@mui/material/TablePagination";
// import ListSummaryCard from "../../common/ListSummaryCard";

// import EditIcon from "@mui/icons-material/EditOutlined";
import { notBlank } from "../../../util/ext";
import { useMediaTable } from "../../common/use-mediaTable";
import { Link, useResolvedPath } from "react-router-dom";
import StatusTableCell from "../../common/StatusTableCell";
import ListSummaryCard from "../../common/ListSummaryCard";
import {
  CircularProgress,
  IconButton,
  List,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Popover,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  AlertContext,
  AlertSeverity,
} from "../../../components/alert/AlertProvider";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import { faCircleMinus } from "@fortawesome/pro-regular-svg-icons";
import { useIsAdmin } from "../../../auth/authUtils";
import OwnersList from "../../modules/browse/OwnersList";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  anthologyLink: {
    color: theme.palette.primary.brandPrimary,
    fontWeight: theme.typography.fontWeightRegular,
  },

  childCountCell: {
    paddingRight: theme.spacing(24),
  },

  contentCell: {
    width: "33%",
    paddingRight: theme.spacing(3),
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  lastUpdateCell: {
    width: "8%",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const RemoveButton = React.forwardRef(({ collectionId, moduleId }, ref) => {
  const alert = useContext(AlertContext);
  const deleteModuleItems = useDeleteModuleItems();
  const handleRemoveFromModule = async event => {
    event.stopPropagation();
    try {
      await deleteModuleItems.mutateAsync({
        id: moduleId,
        ids: [collectionId],
      });
    } catch (err) {
      const messages =
        err?.body && notBlank(err.body.errors)
          ? pluck("message", err.body.errors)
          : [err.message];
      alert.showMessage(messages[0], AlertSeverity.error);
    }
  };

  return (
    <Tooltip ref={ref} title="Remove Anthology From Module">
      <IconButton edge="end" onClick={handleRemoveFromModule} size="large">
        {deleteModuleItems.isPending ? (
          <CircularProgress size={15} />
        ) : (
          <FontAwesomeIcon variant="delete" icon={faCircleMinus} />
        )}
      </IconButton>
    </Tooltip>
  );
});

const CollectionTable = props => {
  const url = useResolvedPath(".").pathname;
  const isAdmin = useIsAdmin();

  const {
    searchState,
    collectionList,
    page,
    onPageChange,
    total = 0,
    onSelectChange,
    orderBy = "name",
    order = "asc",
    onSortChange,
    onScrollVisible,
  } = props;

  const classes = useStyles();
  const navigate = useNavigate();

  const {
    state,
    handleRequestSort,
    handleChangePage,
    handleCollectionRowClick,
    handleCollectionSelectAllClick,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Collection" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center">
                <Checkbox
                  color="primary"
                  indeterminate={
                    state.selected.length > 0 &&
                    state.selected.length < collectionList.length
                  }
                  checked={
                    state.selected.length > 0 &&
                    state.selected.length === collectionList.length
                  }
                  onChange={event =>
                    handleCollectionSelectAllClick(event.target, collectionList)
                  }
                  inputProps={{ "aria-label": "Select" }}
                />
              </TableCell>

              <TableCell key="collection" className={classes.contentCell}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={order}
                  onClick={() => handleRequestSort("name")}
                >
                  Title
                  {orderBy === "name" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell key="date-updated" className={classes.lastUpdateCell}>
                <TableSortLabel
                  active={orderBy === "recordDate"}
                  direction={order}
                  onClick={() => handleRequestSort("recordDate")}
                >
                  Last Update
                  {orderBy === "auditUpdated" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              {isAdmin && (
                <TableCell key="organization">Library/Organization</TableCell>
              )}

              <TableCell key="collection-status">Status</TableCell>

              <TableCell
                key="contentCount"
                align="right"
                className={classes.childCountCell}
              >
                Content Count
              </TableCell>

              <TableCell key="anthology">Anthology</TableCell>

              <TableCell key="parent-modules">Parent Modules</TableCell>

              {/* <TableCell key="modules" className={classes.cell}>
                <TableSortLabel
                  active={orderBy === "modules"}
                  direction={order}
                  onClick={() => handleRequestSort("modules")}
                >
                  Modules
                  {orderBy === "modules" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell> */}

              {/* <TableCell
                key="actions-edit"
                align="center"
                style={{ width: 50 }}
              >
                Edit
              </TableCell> */}
            </TableRow>
          </TableHead>

          <TableBody>
            {collectionList.map(collection => (
              <TableRow
                onClick={() => {
                  const parentAnthologyId = collection.anthology
                    ? collection.anthology.id
                    : "";
                  handleCollectionRowClick(collection.id, parentAnthologyId);
                }}
                hover
                tabIndex={-1}
                key={collection.id}
                role="checkbox"
                aria-checked={any(propEq("id", collection.id), state.selected)}
                selected={any(propEq("id", collection.id), state.selected)}
              >
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    color="primary"
                    checked={any(propEq("id", collection.id), state.selected)}
                    inputProps={{
                      "aria-labelledby": "content-checkbox-" + collection.id,
                    }}
                  />
                </TableCell>

                <TableCell
                  key={"title:" + collection.id}
                  className={classes.contentCell}
                >
                  <Link
                    to={`${url}/${collection.id}`}
                    style={{ color: "#127DA9", fontWeight: "400" }}
                  >
                    <CollectionCard collection={collection} />
                  </Link>
                </TableCell>

                <TableCell key={"date-updated:" + collection.id}>
                  {new Date(collection.auditUpdated).toLocaleDateString()}
                </TableCell>

                {isAdmin && (
                  <TableCell key={"organization: " + collection.id}>
                    <OwnersList media={collection} searchState={searchState} />
                  </TableCell>
                )}

                <StatusTableCell
                  key={("collection-status: ", collection.id)}
                  status={collection.status}
                />

                <TableCell
                  key={"contentCount:" + collection.id}
                  className={classes.childCountCell}
                  align="right"
                >
                  {collection.childCount}
                </TableCell>

                <TableCell key={"anthology:" + collection.id}>
                  {collection.anthology && (
                    <Link
                      to={{
                        pathname: `/creator/anthologies/${collection.anthology.id}`,
                      }}
                      state={{
                        label: "Back to Collections",
                        previousLocation: "/creator/collections",
                      }}
                      className={classes.anthologyLink}
                    >
                      {collection.anthology.name}
                    </Link>
                  )}
                </TableCell>

                <TableCell
                  align="left"
                  key={"parent-modules: " + collection.id}
                >
                  <ListSummaryCard
                    data={collection.parentModules}
                    renderPopover={popoverProps => (
                      <Popover
                        onClick={e => e.stopPropagation()}
                        open={popoverProps.open}
                        anchorEl={popoverProps.anchorEl}
                        onClose={popoverProps.handleClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <List
                          id={`modules-for-${collection.id}`}
                          color="inherit"
                        >
                          {map(
                            module => (
                              <MenuItem
                                key={module.id}
                                onClick={event => {
                                  event.stopPropagation();
                                  navigate(`/creator/modules/${module.id}`, {
                                    state: {
                                      label: "Back to Collections",
                                      previousLocation: "/creator/collections",
                                    },
                                  });
                                }}
                              >
                                <Tooltip
                                  title={`Edit ${module.name}`}
                                  placement="bottom-start"
                                >
                                  <ListItemText
                                    primary={module.name}
                                    primaryTypographyProps={{
                                      noWrap: true,
                                    }}
                                  />
                                </Tooltip>

                                <ListItemSecondaryAction>
                                  <RemoveButton
                                    collectionId={collection.id}
                                    moduleId={module.id}
                                  />
                                </ListItemSecondaryAction>
                              </MenuItem>
                            ),
                            collection.parentModules,
                          )}
                        </List>
                      </Popover>
                    )}
                  />
                </TableCell>

                {/* <TableCell key={"modules:" + collection.id}>
                  <ListSummaryCard data={collection.ancestorModules} />
                </TableCell> */}

                {/* <TableCell
                  key={"actions-edit:" + collection.id}
                  align="center"
                  style={{ width: 50 }}
     
                >
                  <Link to={`${url}/${collection.id}`}>
                    <IconButton className={classes.button} aria-label="edit">
                      <EditIcon />
                    </IconButton>
                  </Link>
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10]}
        component="div"
        count={total}
        rowsPerPage={10}
        page={page}
        onPageChange={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default CollectionTable;
